/**
 * Scannable Code 128 barcode (§12.3) rendered as crisp SVG <rect>s from the pure
 * encoder in lib/barcode.ts. Used on the packing slip to encode the internal
 * Order.id; the pack station reads it to open the order at the bench. A 10-module
 * quiet zone each side keeps it scannable; `print-color-adjust` (in print.css)
 * forces the bars to print solid black.
 */
import { code128Bars } from "../../lib/barcode.js";

export function Barcode({
  value,
  height = 48,
  moduleWidth = 1.7,
  displayValue,
  className,
}: {
  value: string;
  /** Bar height in px. */
  height?: number;
  /** Width of one module in px (≥1.5 keeps it reliably scannable). */
  moduleWidth?: number;
  /** Human-readable text under the bars; pass "" to hide. Defaults to `value`. */
  displayValue?: string;
  className?: string;
}) {
  const { bars, modules } = code128Bars(value);
  const quiet = 10; // modules of quiet zone each side
  const totalModules = modules + quiet * 2;
  const width = totalModules * moduleWidth;
  const text = displayValue === undefined ? value : displayValue;
  const textH = text ? 14 : 0;

  return (
    <span className={"barcode" + (className ? ` ${className}` : "")}>
      <svg
        className="barcode__svg"
        width={width}
        height={height + textH}
        viewBox={`0 0 ${width} ${height + textH}`}
        role="img"
        aria-label={`Barcode ${value}`}
        shapeRendering="crispEdges"
      >
        <rect width={width} height={height + textH} fill="#ffffff" />
        {bars.map((b, i) => (
          <rect
            key={i}
            x={(b.x + quiet) * moduleWidth}
            y={0}
            width={b.width * moduleWidth}
            height={height}
            fill="#000000"
          />
        ))}
        {text && (
          <text
            x={width / 2}
            y={height + textH - 2}
            textAnchor="middle"
            fontFamily="var(--font-mono, monospace)"
            fontSize="11"
            letterSpacing="1.5"
            fill="#000000"
          >
            {text}
          </text>
        )}
      </svg>
    </span>
  );
}
